import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Copy } from "lucide-react";

const empty = { code: "", type: "percent", value: "", label: "", max_uses: "", expires_at: "" };

export function Coupons() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/coupons").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const preview = () => {
    const v = parseFloat(form.value);
    if (!v) return null;
    const discount = form.type === "percent" ? Math.min(99, Math.round(99 * v / 100)) : Math.min(99, v);
    const final = 99 - discount;
    return `₹99 → ₹${final < 1 && final > 0 ? 1 : final}${final === 0 ? " (FREE unlock)" : ""}`;
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/coupons", {
        ...form,
        value: parseFloat(form.value),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create coupon");
    } finally { setBusy(false); }
  };

  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); load(); };
  const remove = async (c) => { if (!window.confirm(`Delete coupon ${c.code}?`)) return; await api.delete(`/admin/coupons/${c.id}`); toast.success("Deleted"); load(); };
  const gen = () => setForm({ ...form, code: "SR" + Math.random().toString(36).slice(2, 8).toUpperCase() });
  const status = (c) => !c.active ? "Inactive" : c.expires_at && c.expires_at < new Date().toISOString() ? "Expired" : c.max_uses && c.used_count >= c.max_uses ? "Exhausted" : "Active";

  return (
    <div className="space-y-6" data-testid="coupons-tab">
      <div>
        <h2 className="font-serif-lux text-xl text-gold-hi">Coupon Codes</h2>
        <p className="text-xs text-muted-warm mt-1">Customers enter a code on the birth form or on the unlock page. 100% off unlocks the image instantly without payment.</p>
      </div>
      <form onSubmit={create} className="glass-panel rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-6 gap-3" data-testid="coupon-form">
        <div className="flex gap-1 sm:col-span-2">
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9-_]/g, "") })} placeholder="CODE e.g. DIWALI50" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm flex-1 uppercase tracking-widest" required minLength={3} data-testid="coupon-code-input" />
          <button type="button" onClick={gen} className="btn-outline-gold text-[10px] px-2" data-testid="coupon-generate-btn">Auto</button>
        </div>
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" data-testid="coupon-type-select">
          <option value="percent">% off</option>
          <option value="flat">₹ off</option>
        </select>
        <input type="number" min="1" max={form.type === "percent" ? 100 : 99} step="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === "percent" ? "e.g. 50" : "e.g. 50"} className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" required data-testid="coupon-value-input" />
        <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Max uses (blank = ∞)" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" data-testid="coupon-max-uses-input" />
        <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" title="Expiry date (optional)" data-testid="coupon-expiry-input" />
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Internal note e.g. Diwali campaign" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm sm:col-span-4" data-testid="coupon-label-input" />
        <div className="text-xs text-gold-hi self-center" data-testid="coupon-preview">{preview()}</div>
        <button disabled={busy} className="btn-gold text-xs disabled:opacity-60" data-testid="coupon-create-btn">{busy ? "…" : "Create Coupon"}</button>
      </form>

      <div className="glass-panel rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-gold text-left">
            <tr><th className="p-3">Code</th><th className="p-3">Discount</th><th className="p-3">Uses</th><th className="p-3">Expires</th><th className="p-3">Status</th><th className="p-3">Note</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-gold/20" data-testid={`coupon-row-${c.code}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-gold-hi tracking-widest font-medium">{c.code}</span>
                    <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Code copied"); }} className="text-muted-warm hover:text-gold-hi" title="Copy" data-testid={`coupon-copy-${c.code}`}><Copy size={12} /></button>
                  </div>
                </td>
                <td className="p-3 text-ivory">{c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`}</td>
                <td className="p-3 text-muted-warm" data-testid={`coupon-uses-${c.code}`}>{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="p-3 text-muted-warm">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-IN") : "—"}</td>
                <td className="p-3">
                  <button onClick={() => toggle(c)} className={`text-[10px] px-2 py-1 rounded ${status(c) === "Active" ? "bg-green-900/40 text-green-300" : "bg-red-900/40 text-red-300"}`} data-testid={`coupon-toggle-${c.code}`}>{status(c)}</button>
                </td>
                <td className="p-3 text-muted-warm text-xs">{c.label}</td>
                <td className="p-3 text-right"><button onClick={() => remove(c)} className="text-red-300 hover:text-red-200" data-testid={`coupon-delete-${c.code}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-warm text-sm" data-testid="coupons-empty">No coupons yet. Create your first one above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
